<script setup lang="ts">
/**
 * 年龄段选择器（家庭教育的可选前置条件）。
 *
 * 两个决定：
 * - 达到上限时**只是禁用**未选中的项，不做"替换最早选中项"的自动行为：
 *   一次点击什么都没发生，用户会以为界面坏了；而悄悄替换掉他上一步的选择，
 *   他会以为是自己点错了。禁用状态本身就是说明。
 * - 输出顺序按契约的 `AGE_BANDS` 排列，而不是点击顺序：这个值会进请求体，
 *   固定顺序让"同一组选择"在任何操作路径下都得到同一个数组，
 *   缓存与日志对比才有意义。
 */

import { computed } from 'vue';
import { AGE_BANDS, type AgeBand } from '@spirit/contracts';

import { cn } from '../../lib/cn';
import { AGE_BAND_LABELS } from './age-band-labels';

const props = withDefaults(
  defineProps<{
    value: AgeBand[];
    max?: number;
    disabled?: boolean;
    label?: string;
    hint?: string;
    idPrefix?: string;
  }>(),
  { max: 4, disabled: false, label: '孩子年龄段', idPrefix: 'age-band' },
);

const emit = defineEmits<{ change: [next: AgeBand[]] }>();

const atLimit = computed(() => props.value.length >= props.max);

function isSelected(band: AgeBand): boolean {
  return props.value.includes(band);
}

function isDisabled(band: AgeBand): boolean {
  return props.disabled || (!isSelected(band) && atLimit.value);
}

function toggle(band: AgeBand): void {
  if (props.disabled) return;
  if (isSelected(band)) {
    emit(
      'change',
      props.value.filter((item) => item !== band),
    );
    return;
  }
  if (atLimit.value) return;
  emit(
    'change',
    AGE_BANDS.filter((item) => props.value.includes(item) || item === band),
  );
}
</script>

<template>
  <div>
    <div class="flex flex-wrap items-baseline gap-x-2">
      <span :id="`${props.idPrefix}-label`" class="text-sm font-medium text-slate-800">
        {{ props.label }}
      </span>
      <span v-if="props.max > 1" class="text-xs text-slate-500">
        可多选（{{ props.value.length }}/{{ props.max }}）
      </span>
    </div>

    <div
      class="mt-2 flex flex-wrap gap-2"
      role="group"
      :aria-labelledby="`${props.idPrefix}-label`"
      :aria-describedby="props.hint === undefined ? undefined : `${props.idPrefix}-hint`"
    >
      <button
        v-for="band in AGE_BANDS"
        :key="band"
        type="button"
        :aria-pressed="isSelected(band) ? 'true' : 'false'"
        :disabled="isDisabled(band)"
        :class="
          cn(
            'rounded-full border px-3 py-1.5 text-sm transition-colors',
            isSelected(band)
              ? 'border-brand-500 bg-brand-500 text-white'
              : 'border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:bg-brand-50',
            isDisabled(band) && 'cursor-not-allowed opacity-50',
          )
        "
        @click="toggle(band)"
      >
        {{ AGE_BAND_LABELS[band] }}
      </button>
    </div>

    <p v-if="props.hint !== undefined" :id="`${props.idPrefix}-hint`" class="mt-1.5 text-xs text-slate-500">
      {{ props.hint }}
    </p>
  </div>
</template>
